import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import {
  initializeFirestore,
  Firestore,
  connectFirestoreEmulator,
  memoryLocalCache,
  memoryLruGarbageCollector,
} from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import {
  getFunctions,
  Functions,
  connectFunctionsEmulator,
  type HttpsCallable,
  type HttpsCallableOptions,
} from 'firebase/functions';
import { httpsCallable, httpsCallableFromURL, registerAuthReady, tokenSourceFromAuth } from '../lib/firebase/authReadyCallable';

/**
 * Resolve the Firebase Auth handler domain.
 *
 * Google sign-in via signInWithRedirect (used on iOS, where Safari blocks
 * popups) needs to stash a one-time state token and read it back when Google
 * returns the user to the app. iOS/WebKit blocks "third-party storage", so when
 * the auth handler lives on a *different* origin than the app
 * (e.g. quoter-saas.firebaseapp.com vs app.onpointgen.com) the token is wiped
 * and the redirect silently loops back to the login screen. Standalone PWAs
 * make this worse because iOS resets their storage on every app switch.
 *
 * Fix: serve the auth handler same-origin with the app. Firebase Hosting
 * automatically exposes the `/__/auth/` handler on every domain connected to
 * the project (including custom domains), so using the current hostname as the
 * authDomain keeps the OAuth round-trip first-party and the state token intact.
 *
 * Local development (localhost / emulators) keeps the env-provided value, which
 * uses popups and is unaffected by the partitioning issue.
 *
 * NOTE: each production host used here must be listed under Firebase Auth
 * Authorized domains and the Google OAuth client's redirect URIs
 * (https://<host>/__/auth/handler), or sign-in will fail with redirect_uri_mismatch.
 */
function resolveAuthDomain(): string | undefined {
  const envAuthDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
  if (typeof window === 'undefined') return envAuthDomain;

  const { hostname } = window.location;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
  if (isLocalhost) return envAuthDomain;

  // Production: the app is only ever served from Firebase Hosting domains for
  // this project, all of which expose /__/auth/ — so the current host is a
  // valid, same-origin auth handler.
  return hostname;
}

// Firebase configuration
// These values are loaded from .env file
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: resolveAuthDomain(),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig);

// Initialize Firebase services
const auth: Auth = getAuth(app);

// Hold every callable (all call sites go through lib/firebase/authReadyCallable) until Firebase Auth has restored the signed-in user, so a deep
// link that fires callables on mount does not send them without an ID token (the 401). `typeof` keeps a test double for `auth` from throwing.
registerAuthReady(() => (typeof auth.authStateReady === 'function' ? auth.authStateReady() : Promise.resolve()), tokenSourceFromAuth(auth));

// Firestore with an IN-MEMORY cache. No IndexedDB persistence. This is
// load-bearing — do NOT re-enable `persistentLocalCache` in this app without
// reading the whole of this comment.
//
// WHY: on-disk persistence requires an exclusive IndexedDB lock, and losing that
// lock DOES NOT fail gracefully. The startup path is caught inline and falls back
// to memory, which is fine and is what the previous version of this comment
// described. The LEASE-REFRESH path is not caught: it throws inside the Firestore
// AsyncQueue, which sets an internal "queue is dead" flag, after which every
// subsequent enqueue asserts `INTERNAL ASSERTION FAILED: Unexpected state (ID:
// b815)`. One `db` instance serves the entire app, so the tab is bricked for
// every Firestore-backed page until the user reloads — and users do not reload,
// they just experience a broken app.
//
// This is measured, not theorised (see #3775). From the `error_events` sink,
// 2026-05-25 -> 2026-08-22:
//   - 313 b815 events = 14.4% of ALL logged client errors
//   - 11 sessions, 2 users (both multi-tab office users; the SDK log
//     "Failed to obtain exclusive access to the persistence layer" appears as the
//     precursor on the poisoning path)
//   - worst single session: 77 events spanning 2026-08-17 -> 2026-08-20. Three
//     days on one poisoned tab, across /projects, /field-service, /crm/leads,
//     /admin/issue-triage and /billing/collections.
//
// The history here is three attempts at the same lock:
//   1. `persistentMultipleTabManager()` — buggy primary-lease election in SDK
//      12.8.0; 460+ "Failed to obtain primary lease" errors and a silently
//      dropped call-log write (`call-log-service.ts` writeBatch resolved against
//      the local cache while waiting for a lease that never came).
//   2. `persistentSingleTabManager()` — no lease election, so THAT failure could
//      not recur. But the IndexedDB lock remained, and with it the b815 poisoning
//      above. It traded a write-loss bug for an availability bug.
//   3. `memoryLocalCache()` (here) — no lock at all, so neither can happen.
//
// WHAT THIS COSTS: offline reads do not survive a page load. Within a live
// session the memory cache serves what is still resident (LRU — see below; it is
// a cache, not a guarantee), but nothing survives a reload, so opening a
// previously-viewed document with no signal no longer works.
//
// That loss is close to theoretical here, because this app never actually
// delivered it: `hooks/survey/useSiteSurvey.ts` already documents "the main app
// has no offline reads", and `pages/Surveys/NewSurvey.tsx` already tells the user
// to "Reopen it once you're back online." `client-tech` has run
// `memoryLocalCache()` all along, and the field survey flow that genuinely needs
// offline lives in `sales-client`.
//
// WHAT THIS DOES NOT COST: survey WRITES. Mutations go through httpsCallable
// (NO CLIENT-DIRECT WRITES rule) and, for offline capture, through the offline-sdk
// MutationQueue + PhotoStore (`lib/survey/offlineEngine.ts`) — a durable IndexedDB
// outbox that is entirely independent of Firestore's own cache. Nothing about
// on-site data capture depends on this setting.
//
// SECURITY BONUS: persistence made every document a signed-in user fetched durable
// on disk, which on a shared field tablet had to be wiped explicitly at sign-out —
// and that wipe silently no-ops when another tab holds the lock (see
// `lib/firebase-auth.ts`). A memory cache dies with the tab, so that residue
// cannot outlive the session in the first place.
const db: Firestore = initializeFirestore(app, {
  // LRU, not the default eager collector. `memoryLocalCache()` with no options
  // evicts a document the moment it stops being part of an active query, so
  // every back-navigation would drop the target and its resume token and re-run
  // the listen as a full query instead of a delta — extra latency and extra
  // billed reads on ordinary navigation. LRU keeps recently-used documents
  // around instead. This has NO bearing on the b815 fix: still no IndexedDB,
  // still no lock.
  localCache: memoryLocalCache({ garbageCollector: memoryLruGarbageCollector() }),
});
const storage: FirebaseStorage = getStorage(app);
const functions: Functions = getFunctions(app);

// Connect to emulators in development mode
const isDev = process.env.NODE_ENV === 'development';
const useEmulators = process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true';

if (isDev && useEmulators) {
  console.log('🔧 Connecting to Firebase Emulators...');

  connectAuthEmulator(auth, 'http://localhost:9099');
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);

  console.log('✅ Connected to Firebase Emulators');
}

// ============================================================================
// Callable routing (WS10 — Cloud Run extraction)
// ============================================================================

/**
 * Base URL of the Investor Relations (P18) Cloud Run router, if IR has been
 * extracted in this environment. Empty/unset means "IR still lives in Cloud
 * Functions", which is the correct default and the safe fallback: the app keeps
 * working with no code change if the env var is missing or the rollout is
 * reverted.
 *
 * Never hardcode the URL — it differs per environment and changes if the
 * service is redeployed under a new name. The origin must also be present in
 * the hosting CSP `connect-src` (firebase.json) or the browser blocks the call.
 */
const IR_ROUTER_URL = (process.env.REACT_APP_IR_ROUTER_URL ?? '').trim();

/** Callables served by the IR router once REACT_APP_IR_ROUTER_URL is set. */
const IR_CALLABLE_PREFIX = 'ir_';

/**
 * Return a callable for `name`, transparently routing to a Cloud Run service
 * when that module has been extracted.
 *
 * This is the single client-side indirection point for the WS10 backend
 * extraction. Call sites keep passing the Cloud Function name they always used
 * (`ir_listInvestors`); this function decides where that name is actually
 * served:
 *
 *   - Extracted module + router URL configured -> `httpsCallableFromURL`
 *     against the router, with the action derived from the name. The Firebase
 *     SDK still attaches the user's ID token and still maps `{result}` /
 *     `{error}`, because the router speaks the callable wire protocol.
 *   - Otherwise -> the ordinary `httpsCallable` against Cloud Functions.
 *
 * Keeping the Cloud Function name as the call-site identifier means the cutover
 * and any rollback need no change to a call site, and no caller has to know
 * which platform currently serves the module. They are NOT a runtime flag flip:
 * CRA inlines REACT_APP_* at build time, so the switch (committed in
 * client/.env.production) takes a rebuild plus a hosting deploy to move either
 * way — 10-15 minutes, not seconds.
 *
 * Emulator note: when the Functions emulator is connected, everything stays on
 * the emulator — a local emulator run must not silently reach a deployed router.
 */
export function getFunctionsForCallable<
  RequestData = unknown,
  ResponseData = unknown,
>(name: string, options?: HttpsCallableOptions): HttpsCallable<RequestData, ResponseData> {
  const useIrRouter =
    IR_ROUTER_URL.length > 0 && name.startsWith(IR_CALLABLE_PREFIX) && !(isDev && useEmulators);

  if (!useIrRouter) {
    return httpsCallable<RequestData, ResponseData>(functions, name, options);
  }

  const action = name.slice(IR_CALLABLE_PREFIX.length);
  const callable = httpsCallableFromURL<Record<string, unknown>, ResponseData>(
    functions,
    IR_ROUTER_URL,
    options,
  );

  // The router dispatches on `action` inside the callable payload, so wrap the
  // caller's data rather than making every call site pass it. `action` is added
  // last so a payload can never shadow the routing key — and if a payload ever
  // does carry its own `action`, fail loudly instead of silently overwriting
  // it. No IR callable takes an `action` field today; this exists so that if
  // one ever does, it surfaces as an immediate error rather than as corrupted
  // data on a cap-table write.
  const routed = (data?: RequestData) => {
    const payload = (data ?? {}) as Record<string, unknown>;
    if ('action' in payload && payload.action !== action) {
      throw new Error(
        `getFunctionsForCallable(${name}): payload already has an 'action' field, ` +
          'which the Cloud Run router uses for dispatch. Rename the payload field.',
      );
    }
    return callable({ ...payload, action });
  };

  // `HttpsCallable` also requires `.stream`. Attach one that throws rather than
  // casting the bare function through `unknown`: the cast alone compiles, and
  // the first caller to use a streaming IR callable — or the next module to
  // copy this pattern for its own extraction — would get an opaque
  // "callable.stream is not a function" at runtime instead of an explanation.
  routed.stream = () => {
    throw new Error(
      `getFunctionsForCallable(${name}): streaming callables are not supported ` +
        'through the Cloud Run router yet. Call the Cloud Function directly, or ' +
        'add streaming support to the router before using .stream().',
    );
  };

  return routed as unknown as HttpsCallable<RequestData, ResponseData>;
}

// Export Firebase services
export { app, auth, db, storage, functions };

// Export types for convenience
export type { User } from 'firebase/auth';
export type { Timestamp, DocumentReference, CollectionReference } from 'firebase/firestore';

